import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, CaretDown, CaretRight, Trash, PencilSimple, Lock, Buildings, House,
  Calculator, Info, Sparkle, FilePdf,
} from "@phosphor-icons/react";
import ValoracionGroupModal from "./ValoracionGroupModal";
import { TIPO_LABEL, TIPO_HINT, fmtEUR, calcularPartida, calcularSubtotalGrupo } from "./valoracionData";

/**
 * Pestaña Valoración. 3 secciones acordeón (Asegurado / Perjudicado / Causante)
 * con sus grupos de partidas. Total consolidado en cabecera con CTA para
 * proponerlo como `importe_tasacion` en el cierre.
 */
export default function ValoracionTab({ exp, onReload }) {
  const grupos = exp.valoracion_grupos || [];

  const totalesPorTipo = { asegurado: 0, perjudicado: 0, causante: 0 };
  for (const g of grupos) {
    if (totalesPorTipo[g.tipo] !== undefined) totalesPorTipo[g.tipo] += calcularSubtotalGrupo(g);
  }
  const totalGeneral = totalesPorTipo.asegurado + totalesPorTipo.perjudicado + totalesPorTipo.causante;

  const ed = exp.extracted_data || {};
  // Aseguradora del expediente (para autocompletar baremos de reparación)
  const aseguradoraRef = ed.aseguradora || exp.detected_aseguradora || null;
  const [aseguradoraId, setAseguradoraId] = useState(null);
  useEffect(() => {
    let cancel = false;
    if (!aseguradoraRef) { setAseguradoraId(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/aseguradoras");
        const items = Array.isArray(data) ? data : (data.items || []);
        const norm = (s) => (s || "").toLowerCase().trim();
        const found = items.find((a) => norm(a.name) === norm(aseguradoraRef)
          || norm(a.nombre_compacto) === norm(aseguradoraRef)
          || norm(a.code) === norm(aseguradoraRef))
          || items.find((a) => norm(a.name).includes(norm(aseguradoraRef)) || norm(aseguradoraRef).includes(norm(a.name)));
        if (!cancel) setAseguradoraId(found?.id || null);
      } catch (e) { /* silencioso */ }
    })();
    return () => { cancel = true; };
  }, [aseguradoraRef]);

  return (
    <div data-testid="valoracion-tab" className="h-full overflow-y-auto bg-surface">
      <div className="p-6 space-y-6 max-w-6xl pb-16">
      {/* Cabecera con total consolidado */}
      <header className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-5 shadow-lg ring-1 ring-amber-400/40">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-amber-400 font-bold mb-2">
              <Calculator className="w-4 h-4" weight="fill" />
              Valoración del siniestro
            </div>
            <h2 className="font-display font-bold text-4xl tracking-tight tabular-nums text-white" data-testid="valoracion-total">
              {fmtEUR(totalGeneral)}
            </h2>
            <div className="text-[12px] text-slate-300 mt-1">
              {grupos.length} grupo{grupos.length !== 1 ? "s" : ""} · Suma de Asegurado + Perjudicado + Causante
            </div>
          </div>
          <div className="flex items-stretch gap-2 flex-wrap">
            <Pill label="Asegurado" value={fmtEUR(totalesPorTipo.asegurado)} testid="total-asegurado" />
            <Pill label="Perjudicado" value={fmtEUR(totalesPorTipo.perjudicado)} testid="total-perjudicado" />
            <Pill label="Causante" value={fmtEUR(totalesPorTipo.causante)} testid="total-causante" />
          </div>
        </div>
        {totalGeneral > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-700 flex items-center justify-between text-[12px]">
            <div className="text-amber-300 inline-flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" weight="bold" />
              Al cerrar el siniestro este total se propondrá como <b className="text-white">importe de tasación</b> (editable).
            </div>
          </div>
        )}
      </header>

      {/* 3 secciones acordeón */}
      {["asegurado", "perjudicado", "causante"].map((tipo) => (
        <TipoSection
          key={tipo}
          tipo={tipo}
          grupos={grupos.filter((g) => g.tipo === tipo)}
          subtotal={totalesPorTipo[tipo]}
          expedienteId={exp.id}
          implicados={exp.implicados || []}
          aseguradoraId={aseguradoraId}
          onReload={onReload}
        />
      ))}

      {/* Concurrencia con otras aseguradoras (art. 32 LCS) */}
      <ConcurrenciaSection exp={exp} onReload={onReload} />
      </div>
    </div>
  );
}

function Pill({ label, value, testid }) {
  return (
    <div className="bg-slate-800 rounded-lg px-3 py-2 border border-slate-700 min-w-[120px]">
      <div className="text-[9px] uppercase tracking-widest text-amber-400 font-bold">{label}</div>
      <div className="font-display font-bold text-base text-white tabular-nums" data-testid={testid}>{value}</div>
    </div>
  );
}

// =====================================================================
// Sección por tipo (acordeón)
// =====================================================================

function TipoSection({ tipo, grupos, subtotal, expedienteId, implicados = [], aseguradoraId, onReload }) {
  const [open, setOpen] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const importInputRef = useRef(null);
  // ID del implicado al que se le imputa el próximo presupuesto importado.
  // Solo aplica cuando tipo=perjudicado|causante y hay implicados de ese
  // tipo listados en el expediente. Si el usuario deja "colectivo" (""),
  // el grupo se crea sin `implicado_id` (imputado al colectivo).
  const [importImplicadoId, setImportImplicadoId] = useState("");
  const implicadosDelTipo = (tipo === "perjudicado" || tipo === "causante")
    ? implicados.filter((it) => it.tipo === tipo && (it.nombre || "").trim())
    : [];
  const showImplicadoPicker = implicadosDelTipo.length > 0;

  // Aviso vía notificación: en valoraciones a Perjudicado las compañías
  // suelen aplicar valor real (con depreciación). Si hay grupos con
  // partidas sin depreciar, avisamos. Si se corrige, la notificación
  // desaparece automáticamente.
  const TOAST_ID = `warn-no-depreciacion-${expedienteId}`;
  const missingDepreciacion = tipo === "perjudicado" && grupos.some((g) => {
    if (g.excluir) return false;
    const partidas = g.partidas || [];
    if (partidas.length === 0) return false;
    if (!g.aplicar_depreciaciones) return true;
    return partidas.some((p) => !p.depreciacion_pct || Number(p.depreciacion_pct) <= 0);
  });

  useEffect(() => {
    if (tipo !== "perjudicado") return;
    if (missingDepreciacion) {
      toast.warning(
        "He visto que no estás depreciando en alguna partida de la valoración de perjudicados. Normalmente las compañías aplican el valor real en estas valoraciones, ¿estás segur@ de continuar?",
        { id: TOAST_ID, duration: Infinity, dismissible: true, closeButton: true },
      );
    } else {
      toast.dismiss(TOAST_ID);
    }
    // Importante: no usar cleanup con dismiss; en React StrictMode (dev)
    // el unmount inmediato cancelaría el toast antes de renderizarlo.
  }, [missingDepreciacion, tipo, TOAST_ID]);

  const addGrupo = async (payload) => {
    await api.post(`/expedientes/${expedienteId}/valoracion/grupos`, payload);
    onReload?.();
  };
  const updateGrupo = async (payload) => {
    await api.put(`/expedientes/${expedienteId}/valoracion/grupos/${editing.id}`, payload);
    setEditing(null);
    onReload?.();
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // permitir re-subir el mismo archivo
    if (!file) return;
    setImporting(true); setImportMsg(null);
    try {
      const fd = new FormData();
      fd.append("tipo", tipo);
      if (tipo !== "causante") fd.append("interes", "continente");
      // Si el usuario ha seleccionado un implicado concreto (perjudicado
      // o causante específico), lo mandamos para que el grupo creado se
      // impute a ÉL y no al colectivo.
      if (importImplicadoId) fd.append("implicado_id", importImplicadoId);
      fd.append("file", file);
      const { data } = await api.post(
        `/expedientes/${expedienteId}/valoracion/import-presupuesto`, fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportMsg({
        ok: true,
        text: `${data.extracted?.partidas_count || data.grupo.partidas.length} partidas importadas` +
              (data.extracted?.proveedor ? ` de ${data.extracted.proveedor}` : ""),
      });
      onReload?.();
      setTimeout(() => setImportMsg(null), 4500);
    } catch (err) {
      setImportMsg({
        ok: false,
        text: err?.response?.data?.detail || "Error en la extracción",
      });
    } finally { setImporting(false); }
  };

  return (
    <section data-testid={`tipo-section-${tipo}`} className="border border-slate-200 rounded-2xl bg-white overflow-hidden">
      <header
        onClick={() => setOpen(!open)}
        className="cursor-pointer flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-amber-50/50 to-white hover:bg-amber-50 transition-colors border-b border-slate-200"
      >
        <div className="flex items-center gap-2.5">
          {open ? <CaretDown className="w-4 h-4 text-amber-700" weight="bold" /> : <CaretRight className="w-4 h-4 text-amber-700" weight="bold" />}
          <div>
            <div className="font-display font-bold text-base text-slate-900 tracking-tight">
              {TIPO_LABEL[tipo]}
              <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-600 font-bold align-middle">
                {grupos.length} grupo{grupos.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="text-[11px] text-muted mt-0.5">{TIPO_HINT[tipo]}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="font-display font-bold text-lg text-slate-900 tabular-nums">
            {fmtEUR(subtotal)}
          </div>
          <input
            ref={importInputRef}
            type="file"
            accept="application/pdf"
            onChange={onImportFile}
            data-testid={`import-input-${tipo}`}
            className="hidden"
          />
          {showImplicadoPicker && (
            <select
              value={importImplicadoId}
              onChange={(e) => setImportImplicadoId(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              data-testid={`import-implicado-select-${tipo}`}
              title={`Al importar un PDF, imputar el presupuesto a este ${tipo}`}
              className="text-[11px] border border-amber-300 bg-white text-slate-800 rounded-md h-7 px-1.5 max-w-[160px] truncate"
            >
              <option value="">Colectivo ({tipo === "perjudicado" ? "perjudicados" : "causantes"})</option>
              {implicadosDelTipo.map((it) => (
                <option key={it.id} value={it.id}>
                  {(it.nombre || "").length > 22 ? `${(it.nombre || "").slice(0, 22)}…` : it.nombre}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); importInputRef.current?.click(); }}
            disabled={importing}
            data-testid={`import-presupuesto-${tipo}`}
            className="inline-flex items-center gap-1 bg-white text-amber-700 border border-amber-300 text-[11px] font-semibold rounded-md px-2.5 py-1.5 hover:bg-amber-50 disabled:opacity-60"
            title="Importar presupuesto en PDF · la IA extrae las partidas"
          >
            {importing ? (
              <Sparkle className="w-3.5 h-3.5 animate-pulse" weight="fill" />
            ) : (
              <FilePdf className="w-3.5 h-3.5" weight="bold" />
            )}
            {importing ? "Extrayendo…" : "Importar PDF"}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setEditing(null); setModalOpen(true); }}
            data-testid={`add-grupo-${tipo}`}
            className="inline-flex items-center gap-1 bg-brand text-white text-[11px] font-semibold rounded-md px-2.5 py-1.5 hover:bg-brand-strong"
          >
            <Plus className="w-3.5 h-3.5" weight="bold" /> Añadir grupo
          </button>
        </div>
      </header>

      {importMsg && (
        <div
          data-testid={`import-msg-${tipo}`}
          className={`px-5 py-2 text-[12px] border-b ${importMsg.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}
        >
          {importMsg.ok ? "✨ " : "⚠ "}{importMsg.text}
        </div>
      )}

      {open && (
        <div className="p-4 space-y-3">
          {grupos.length === 0 && (
            <div className="text-center py-6 text-[12px] text-muted border border-dashed border-slate-200 rounded-lg">
              Sin grupos. Pulsa <b>Añadir grupo</b> para empezar a valorar.
            </div>
          )}
          {grupos.map((g) => (
            <GrupoCard
              key={g.id}
              grupo={g}
              expedienteId={expedienteId}
              implicados={implicados}
              aseguradoraId={aseguradoraId}
              onEdit={() => { setEditing(g); setModalOpen(true); }}
              onReload={onReload}
            />
          ))}
        </div>
      )}

      <ValoracionGroupModal
        open={modalOpen}
        tipo={tipo}
        initial={editing}
        implicados={implicados}
        aseguradoraId={aseguradoraId}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={editing ? updateGrupo : addGrupo}
      />
    </section>
  );
}

// =====================================================================
// Tarjeta de un grupo (con tabla de partidas dentro)
// =====================================================================

function GrupoCard({ grupo, expedienteId, onEdit, onReload, implicados = [], aseguradoraId = null }) {
  const [busy, setBusy] = useState(false);

  const subtotal = calcularSubtotalGrupo(grupo);
  const implicadoNombre = grupo.implicado_id
    ? (implicados.find((i) => i.id === grupo.implicado_id)?.nombre || "(implicado no encontrado)")
    : null;

  const addPartida = async () => {
    setBusy(true);
    try {
      await api.post(`/expedientes/${expedienteId}/valoracion/grupos/${grupo.id}/partidas`, {
        descripcion: "", cantidad: 1, precio_unitario: 0,
      });
      onReload?.();
    } finally { setBusy(false); }
  };
  const deleteGrupo = async () => {
    if (!window.confirm("¿Eliminar este grupo y todas sus partidas?")) return;
    await api.delete(`/expedientes/${expedienteId}/valoracion/grupos/${grupo.id}`);
    onReload?.();
  };

  const [importerOpen, setImporterOpen] = useState(false);

  const importBaremoPartida = async (item) => {
    setBusy(true);
    try {
      await api.post(`/expedientes/${expedienteId}/valoracion/grupos/${grupo.id}/partidas`, {
        descripcion: item.descripcion,
        cantidad: 1,
        precio_unitario: item.precio_unitario || (Number(item.precio_mano_obra || 0) + Number(item.precio_material || 0)) || 0,
        notas: [item.codigo, item.capitulo, item.unidad ? `${item.unidad}` : null, item.baremo_name].filter(Boolean).join(" · "),
      });
      onReload?.();
    } finally { setBusy(false); }
  };

  const interesIcon = grupo.interes === "continente" ? Buildings : grupo.interes === "contenido" ? House : null;

  return (
    <article
      data-testid={`grupo-${grupo.id}`}
      className={`border rounded-xl bg-white ${grupo.excluir ? "border-slate-200 opacity-60" : "border-amber-200/70"}`}
    >
      <header className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <div className="flex items-start gap-3 min-w-0">
          {interesIcon && (
            <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 text-amber-700 inline-flex items-center justify-center">
              {interesIcon === Buildings ? <Buildings className="w-4 h-4" weight="fill" /> : <House className="w-4 h-4" weight="fill" />}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-[13px] text-slate-900">
                {grupo.nombre_grupo || (grupo.cobertura || "Sin cobertura")}
              </span>
              {grupo.interes && (
                <span className="text-[9px] uppercase tracking-widest font-bold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">
                  {grupo.interes}
                </span>
              )}
              {implicadoNombre && (
                <span className="text-[10px] uppercase tracking-wider font-bold text-violet-700 bg-violet-100 rounded px-1.5 py-0.5" data-testid={`grupo-${grupo.id}-implicado`}>
                  {grupo.tipo === "causante" ? "Causante" : "Perjudicado"}: {implicadoNombre}
                </span>
              )}
              {grupo.cobertura && grupo.nombre_grupo && grupo.cobertura !== grupo.nombre_grupo && (
                <span className="text-[11px] text-muted">· {grupo.cobertura}</span>
              )}
              {grupo.excluir && (
                <span className="text-[9px] uppercase tracking-widest font-bold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                  <Lock className="w-2.5 h-2.5" weight="bold" /> Excluido
                </span>
              )}
              {grupo.resolucion === "indemnizacion" && (
                <span
                  data-testid={`grupo-${grupo.id}-resolucion-indemniza`}
                  className="text-[9px] uppercase tracking-widest font-bold text-white bg-emerald-600 rounded px-1.5 py-0.5"
                >
                  Indemniza
                </span>
              )}
              {grupo.resolucion === "asistencia" && (
                <span
                  data-testid={`grupo-${grupo.id}-resolucion-asistencia`}
                  className="text-[9px] uppercase tracking-widest font-bold text-white bg-sky-600 rounded px-1.5 py-0.5"
                >
                  Asistencia cía
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-[11px] text-muted mt-0.5">
              {grupo.limite != null && <span>Límite: <b className="text-slate-700">{fmtEUR(grupo.limite)}</b></span>}
              {grupo.franquicia != null && grupo.franquicia > 0 && (
                <span>Franquicia: <b className="text-slate-700">{grupo.franquicia_tipo === "pct" ? `${grupo.franquicia}%` : fmtEUR(grupo.franquicia)}</b></span>
              )}
              {grupo.con_baremos && <span className="text-amber-700">· con baremos</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right">
            <div className="text-[9px] uppercase tracking-widest text-muted font-bold">Subtotal</div>
            <div className="font-display font-bold text-[15px] text-slate-900 tabular-nums" data-testid={`subtotal-${grupo.id}`}>
              {fmtEUR(subtotal)}
            </div>
          </div>
          <button onClick={onEdit} data-testid={`edit-grupo-${grupo.id}`}
            className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Editar">
            <PencilSimple className="w-4 h-4" weight="bold" />
          </button>
          <button onClick={deleteGrupo} data-testid={`delete-grupo-${grupo.id}`}
            className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600" title="Eliminar">
            <Trash className="w-4 h-4" weight="bold" />
          </button>
        </div>
      </header>

      {!grupo.excluir && (
        <PartidasTable
          grupo={grupo}
          expedienteId={expedienteId}
          onReload={onReload}
        />
      )}

      <footer className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
        <div className="inline-flex items-center gap-3">
          <button
            onClick={addPartida}
            disabled={busy || grupo.excluir}
            data-testid={`add-partida-${grupo.id}`}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand hover:text-brand-strong disabled:opacity-40"
          >
            <Plus className="w-3.5 h-3.5" weight="bold" /> Añadir partida
          </button>
          {aseguradoraId && (
            <button
              onClick={() => setImporterOpen(true)}
              disabled={busy || grupo.excluir}
              data-testid={`import-baremo-${grupo.id}`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-900 disabled:opacity-40"
              title="Buscar partida en los baremos de reparación de la aseguradora"
            >
              <Sparkle className="w-3.5 h-3.5" weight="fill" /> Importar de baremo
            </button>
          )}
        </div>
        <div className="text-[10px] text-muted">
          {(grupo.partidas || []).length} partida{(grupo.partidas || []).length !== 1 ? "s" : ""}
        </div>
      </footer>

      {importerOpen && (
        <BaremoImporterModal
          aseguradoraId={aseguradoraId}
          initialBaremoId={grupo.baremo_rep_id || ""}
          onClose={() => setImporterOpen(false)}
          onPick={async (item) => { await importBaremoPartida(item); }}
        />
      )}
    </article>
  );
}

// =====================================================================
// Modal de búsqueda + navegación de partidas en los baremos de reparación
// =====================================================================
function BaremoImporterModal({ aseguradoraId, initialBaremoId = "", onClose, onPick }) {
  const [baremos, setBaremos] = useState([]);
  const [baremoId, setBaremoId] = useState(initialBaremoId); // "" = todos
  const [capitulos, setCapitulos] = useState([]);
  const [cap, setCap] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  // Cargar baremos activos al abrir
  useEffect(() => {
    inputRef.current?.focus();
    (async () => {
      try {
        const { data } = await api.get(`/aseguradoras/${aseguradoraId}/baremos-reparacion`);
        const activos = (data.baremos || []).filter((b) => b.activa && b.status === "ready");
        setBaremos(activos);
        // Si solo hay uno, lo preselecciono para que se vea ya la lista
        if (activos.length === 1) setBaremoId(activos[0].id);
      } catch (e) { setBaremos([]); }
    })();
  }, [aseguradoraId]);

  // Cargar capítulos cuando se elige un baremo concreto
  useEffect(() => {
    if (!baremoId) { setCapitulos([]); setCap(""); return; }
    (async () => {
      try {
        const { data } = await api.get(
          `/aseguradoras/${aseguradoraId}/baremos-reparacion/${baremoId}/partidas?limit=1`,
        );
        setCapitulos(data.capitulos || []);
      } catch (e) { setCapitulos([]); }
    })();
    setCap("");
  }, [aseguradoraId, baremoId]);

  // Cargar partidas según filtros (con debounce sobre q)
  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        if (baremoId) {
          // Navegación por baremo concreto (acepta q vacío)
          const params = new URLSearchParams();
          if (q) params.set("q", q);
          if (cap) params.set("capitulo", cap);
          params.set("limit", "300");
          const { data } = await api.get(
            `/aseguradoras/${aseguradoraId}/baremos-reparacion/${baremoId}/partidas?${params}`,
          );
          const bname = baremos.find((b) => b.id === baremoId)?.name || "";
          setItems((data.partidas || []).map((p) => ({ ...p, baremo_name: bname })));
        } else if (q.length >= 2) {
          // Búsqueda global por texto en todos los baremos activos
          const params = new URLSearchParams({ aseguradora_id: aseguradoraId, q, limit: "30" });
          if (cap) params.set("capitulo", cap);
          const { data } = await api.get(`/baremos-reparacion/autocomplete?${params}`);
          setItems(data.items || []);
        } else {
          setItems([]);
        }
      } catch (e) { setItems([]); } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, cap, baremoId, aseguradoraId, baremos]);

  const showEmpty = !baremoId && q.length < 2;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 p-4 pt-[10vh]" onClick={onClose}>
      <div className="bg-white rounded-lg w-full max-w-3xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="baremo-importer-modal">
        <div className="px-5 pt-4 pb-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-bold inline-flex items-center gap-2">
            <Sparkle className="w-4 h-4 text-amber-600" weight="fill" /> Importar partida del baremo
          </h2>
          <button onClick={onClose} className="text-muted hover:text-foreground" data-testid="baremo-importer-close">✕</button>
        </div>
        <div className="px-5 py-3 space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr] gap-2">
            <select
              value={baremoId}
              onChange={(e) => setBaremoId(e.target.value)}
              data-testid="baremo-importer-baremo"
              className="text-[13px] border border-border rounded-md px-2 py-1.5 bg-white"
            >
              <option value="">Todos los baremos activos</option>
              {baremos.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.n_partidas ? `· ${b.n_partidas} partidas` : ""}
                </option>
              ))}
            </select>
            <select
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              disabled={!baremoId || capitulos.length === 0}
              data-testid="baremo-importer-capitulo"
              className="text-[13px] border border-border rounded-md px-2 py-1.5 bg-white disabled:bg-slate-50 disabled:text-muted"
            >
              <option value="">{capitulos.length ? "Todos los capítulos" : "—"}</option>
              {capitulos.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={baremoId
              ? "Filtrar por descripción, código o capítulo…"
              : "Busca por descripción, código o capítulo (mín. 2 caracteres)…"}
            data-testid="baremo-importer-search"
            className="w-full text-[13px]"
          />
          {baremos.length === 0 && (
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="baremo-importer-empty-aseguradora">
              Esta aseguradora aún no tiene baremos de reparación activos. Súbelos desde <b>Aseguradoras → Baremos de Reparación</b>.
            </div>
          )}
        </div>
        <div className="max-h-[55vh] overflow-auto px-2 pb-3" data-testid="baremo-importer-results">
          {showEmpty ? (
            <div className="text-center text-muted text-[12px] py-8">Selecciona un baremo del desplegable o escribe al menos 2 caracteres para buscar.</div>
          ) : loading ? (
            <div className="text-center text-muted text-[12px] py-8">Buscando…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-muted text-[12px] py-8">Sin coincidencias.</div>
          ) : (
            <>
              {items.length >= 300 && baremoId && (
                <div className="text-[10.5px] text-amber-700 px-2 pb-1.5">Mostrando las primeras 300 partidas — afina con el buscador o el capítulo.</div>
              )}
              {items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => { onPick(it); onClose(); }}
                  data-testid={`baremo-importer-pick-${it.id}`}
                  className="w-full text-left bg-white border border-border rounded-md p-2.5 mb-1.5 hover:border-amber-400 hover:bg-amber-50/40 transition-colors"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    {it.capitulo && <span className="text-[9px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-bold">{it.capitulo}</span>}
                    {it.codigo && <span className="text-[10px] font-mono text-muted">{it.codigo}</span>}
                    <span className="font-semibold text-[12.5px] flex-1 min-w-0 truncate">{it.descripcion}</span>
                    <span className="font-mono font-bold text-[13px] text-amber-700">
                      {it.precio_unitario != null
                        ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(it.precio_unitario)
                        : "—"} {it.unidad && <span className="text-muted text-[10px]">/{it.unidad}</span>}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted mt-0.5">
                    {it.baremo_name}
                    {(it.precio_mano_obra || it.precio_material) && (
                      <span> · M.O. {fmtEUR(it.precio_mano_obra || 0)} · Material {fmtEUR(it.precio_material || 0)}</span>
                    )}
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Tabla de partidas (descripción + cant + PU + dep% + IVA% + recl + total)
// =====================================================================

function PartidasTable({ grupo, expedienteId, onReload }) {
  const showDep = grupo.aplicar_depreciaciones;
  const showIva = grupo.introducir_iva;
  const showRecl = grupo.introducir_reclamados;
  const partidas = grupo.partidas || [];

  if (partidas.length === 0) {
    return (
      <div className="px-4 py-5 text-center text-[12px] text-muted">
        Sin partidas. Pulsa <b>Añadir partida</b> abajo para empezar.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-[9px] uppercase tracking-widest text-muted font-bold border-b border-slate-100">
            <th className="px-4 py-2 text-left min-w-[280px]">Descripción</th>
            <th className="px-2 py-2 text-right w-16">Cant.</th>
            <th className="px-2 py-2 text-right w-24">P. unit.</th>
            {showDep && <th className="px-2 py-2 text-right w-16">Depr.%</th>}
            {showIva && <th className="px-2 py-2 text-right w-16">IVA%</th>}
            {showRecl && <th className="px-2 py-2 text-right w-24">Reclamado</th>}
            <th className="px-3 py-2 text-right w-28">Total</th>
            <th className="w-8"></th>
          </tr>
        </thead>
        <tbody>
          {partidas.map((p) => (
            <PartidaRow
              key={p.id}
              partida={p}
              grupoId={grupo.id}
              expedienteId={expedienteId}
              showDep={showDep}
              showIva={showIva}
              showRecl={showRecl}
              onReload={onReload}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PartidaRow({ partida, grupoId, expedienteId, showDep, showIva, showRecl, onReload }) {
  const [draft, setDraft] = useState(partida);
  const descInputRef = useRef(null);
  // Solo re-sincronizar el draft si el usuario NO está escribiendo en este input.
  // Antes el draft se reseteaba en cada recarga del expediente y los caracteres
  // tecleados se perdían si el padre hacía un setExp() durante la escritura.
  useEffect(() => {
    const ae = typeof document !== "undefined" ? document.activeElement : null;
    const tag = ae?.tagName;
    const isTypingHere = ae && (tag === "INPUT" || tag === "TEXTAREA") &&
      ae.closest?.(`[data-testid="partida-${partida.id}"]`);
    if (isTypingHere) return;
    setDraft(partida);
  }, [partida]);
  const [saving, setSaving] = useState(false);

  const total = calcularPartida(draft);

  const save = async (next) => {
    setSaving(true);
    try {
      await api.put(`/expedientes/${expedienteId}/valoracion/grupos/${grupoId}/partidas/${partida.id}`, next);
      onReload?.();
    } finally { setSaving(false); }
  };
  const onBlurField = (field, value) => {
    const cleaned = value === "" ? null : (field === "descripcion" || field === "notas" ? value : Number(value));
    if (cleaned === partida[field]) return;
    save({ [field]: cleaned });
  };
  const remove = async () => {
    await api.delete(`/expedientes/${expedienteId}/valoracion/grupos/${grupoId}/partidas/${partida.id}`);
    onReload?.();
  };

  const cell = "px-2 py-1.5 text-right";
  const inp = "w-full !text-[13px] !py-1.5 !px-2 text-right tabular-nums";

  return (
    <tr className="border-b border-slate-100 hover:bg-amber-50/30 align-top" data-testid={`partida-${partida.id}`}>
      <td className="px-4 py-1.5 min-w-[280px]">
        <textarea
          ref={descInputRef}
          rows={1}
          value={draft.descripcion || ""}
          onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })}
          onBlur={(e) => onBlurField("descripcion", e.target.value)}
          placeholder="Descripción de la partida (sin límite de caracteres)"
          data-testid={`p-desc-${partida.id}`}
          className="w-full !text-[13px] !py-1.5 !px-2 leading-snug resize-y min-h-[34px]"
          style={{ height: "auto" }}
          onInput={(e) => {
            e.target.style.height = "auto";
            e.target.style.height = e.target.scrollHeight + "px";
          }}
        />
      </td>
      <td className={cell}>
        <input type="number" min="0" step="0.01"
          value={draft.cantidad ?? ""}
          onChange={(e) => setDraft({ ...draft, cantidad: e.target.value === "" ? null : Number(e.target.value) })}
          onBlur={(e) => onBlurField("cantidad", e.target.value)}
          data-testid={`p-cant-${partida.id}`} className={inp} />
      </td>
      <td className={cell}>
        <input type="number" min="0" step="0.01"
          value={draft.precio_unitario ?? ""}
          onChange={(e) => setDraft({ ...draft, precio_unitario: e.target.value === "" ? null : Number(e.target.value) })}
          onBlur={(e) => onBlurField("precio_unitario", e.target.value)}
          data-testid={`p-pu-${partida.id}`} className={inp} />
      </td>
      {showDep && (
        <td className={cell}>
          <input type="number" min="0" max="100" step="0.1"
            value={draft.depreciacion_pct ?? ""}
            onChange={(e) => setDraft({ ...draft, depreciacion_pct: e.target.value === "" ? null : Number(e.target.value) })}
            onBlur={(e) => onBlurField("depreciacion_pct", e.target.value)}
            data-testid={`p-dep-${partida.id}`} className={inp} />
        </td>
      )}
      {showIva && (
        <td className={cell}>
          <input type="number" min="0" max="100" step="0.1"
            value={draft.iva_pct ?? ""}
            onChange={(e) => setDraft({ ...draft, iva_pct: e.target.value === "" ? null : Number(e.target.value) })}
            onBlur={(e) => onBlurField("iva_pct", e.target.value)}
            data-testid={`p-iva-${partida.id}`} className={inp} />
        </td>
      )}
      {showRecl && (
        <td className={cell}>
          <input type="number" min="0" step="0.01"
            value={draft.importe_reclamado ?? ""}
            onChange={(e) => setDraft({ ...draft, importe_reclamado: e.target.value === "" ? null : Number(e.target.value) })}
            onBlur={(e) => onBlurField("importe_reclamado", e.target.value)}
            data-testid={`p-recl-${partida.id}`} className={inp} />
        </td>
      )}
      <td className="px-3 py-1.5 text-right font-display font-bold text-slate-900 tabular-nums" data-testid={`p-total-${partida.id}`}>
        {fmtEUR(total)}
      </td>
      <td className="px-1 text-center">
        <button onClick={remove} data-testid={`p-del-${partida.id}`}
          className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-600">
          <Trash className="w-3.5 h-3.5" weight="bold" />
        </button>
      </td>
    </tr>
  );
}


/**
 * Formulario de concurrencia calcado 1:1 de la calculadora de Innoku
 * (https://innoku.com/calculadora-de-concurrencia-de-seguros-hogar/).
 *
 * INPUTS (los que teclea el perito):
 *   1) % Participación de la Vivienda  (guardado como participantes[0].coeficiente_participacion)
 *   2) Aseguradora concurrente          (nombre — participantes[0].aseguradora_nombre)
 *   3) Nº de póliza concurrente         (participantes[0].poliza)
 *   4) Total Capital asegurado Vivienda (capital_propio)
 *   5) Total Capital asegurado Comunidad(participantes[0].capital)
 *   6) TOTAL Daños                       (base_importe)
 *
 * OUTPUTS (calculados y mostrados como readonly):
 *   • % Participación de la Comunidad     = 100 − %Vivienda
 *   • Capital asegurado de la Vivienda    = Cap.Vivienda (bruto)
 *   • Capital asegurado Total             = Cap.Vivienda + Cap.Comunidad × %Vivienda
 *   • % Asumido por la Vivienda           = Cap.Vivienda / Capital asegurado Total
 *   • % Asumido por la Comunidad          = 100 − %Asumido Vivienda
 *   • % Asumido Total                     = 100
 *   • Importe a pagar Vivienda            = TOTAL Daños − Importe Comunidad (por resta)
 *   • Importe a pagar Comunidad           = TOTAL Daños × (Cap.Comunidad×%Vivienda / Cap.Total)
 */
function ConcurrenciaInnokuForm({ state, setState, persist, updateParticipante, aseguradoraPropia }) {
  // Aseguramos que exista participantes[0]
  const participantes = state.participantes || [];
  const p0 = participantes[0] || { aseguradora_nombre: "", poliza: "", capital: 0, coeficiente_participacion: null };
  const ensureFirst = (patch) => {
    if (participantes.length === 0) {
      setState((s) => ({ ...s, participantes: [{ aseguradora_nombre: "", poliza: "", capital: 0, coeficiente_participacion: null, ...patch }] }));
    } else {
      updateParticipante(0, patch);
    }
  };

  // Inputs (números tal como el usuario los teclea, sin normalizar)
  const capViv = Number(state.capital_propio) || 0;
  const capCom = Number(p0.capital) || 0;
  const danos = Number(state.base_importe) || 0;
  const coefViv = p0.coeficiente_participacion == null ? 0 : Number(p0.coeficiente_participacion) || 0;
  const coefVivPct = coefViv * 100; // ej. 0,0135 → 1,35
  const coefComPct = coefViv > 0 ? 100 - coefVivPct : 0;

  // Cálculos (idénticos a Innoku)
  const capComEnViv = capCom * coefViv;
  const capTotal = capViv + capComEnViv;
  const pctAsumidoViv = capTotal > 0 ? (capViv / capTotal) * 100 : 0;
  const pctAsumidoCom = capTotal > 0 ? (capComEnViv / capTotal) * 100 : 0;
  // Importes: comunidad calculado, propia por RESTA para cuadrar céntimos
  const importeCom = capTotal > 0
    ? Math.round((danos * capComEnViv / capTotal) * 100) / 100
    : 0;
  const importeViv = capTotal > 0
    ? Math.round((danos - importeCom) * 100) / 100
    : 0;

  const fmtPct = (v) => `${v.toFixed(2).replace(".", ",")} %`;

  return (
    <div className="space-y-5">
      <div className="text-[11px] text-muted italic">* Cumplimentar los campos en blanco</div>

      {/* — SECCIÓN 1: % Participación — */}
      <div>
        <div className="font-display font-bold text-sm mb-2">% Participación</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-[11px]">% Participación de la Vivienda <span className="text-red-600">*</span></span>
            <input
              type="number" min="0" max="100" step="0.001" placeholder="Ej. 1,35"
              value={p0.coeficiente_participacion == null ? "" : (p0.coeficiente_participacion * 100)}
              onChange={(e) => ensureFirst({
                coeficiente_participacion: e.target.value === "" ? null : Number(e.target.value) / 100,
              })}
              onBlur={() => persist()}
              data-testid="concurrencia-pct-vivienda"
              className="border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-[11px]">% Participación de la Comunidad</span>
            <input
              type="text" readOnly
              value={coefViv > 0 ? fmtPct(coefComPct) : ""}
              data-testid="concurrencia-pct-comunidad"
              className="border border-border rounded-md px-3 py-2 text-sm bg-slate-50 text-muted"
            />
          </label>
        </div>
      </div>

      {/* — SECCIÓN 2: Aseguradora concurrente (nombre + póliza) — */}
      <div>
        <div className="font-display font-bold text-sm mb-2">Aseguradora concurrente</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-[11px]">Compañía contraria <span className="text-red-600">*</span></span>
            <input
              type="text" placeholder="Ej. Mapfre, Ocaso, REALE…"
              value={p0.aseguradora_nombre || ""}
              onChange={(e) => ensureFirst({ aseguradora_nombre: e.target.value })}
              onBlur={() => persist()}
              data-testid="concurrencia-aseguradora-contraria"
              className="border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-[11px]">Nº de póliza (opcional)</span>
            <input
              type="text" placeholder="Ej. POL-123456"
              value={p0.poliza || ""}
              onChange={(e) => ensureFirst({ poliza: e.target.value })}
              onBlur={() => persist()}
              data-testid="concurrencia-poliza-contraria"
              className="border border-border rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
            />
          </label>
        </div>
      </div>

      {/* — SECCIÓN 3: Total Capital asegurado — */}
      <div>
        <div className="font-display font-bold text-sm mb-2">Total Capital asegurado</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-[11px]">Total Capital asegurado de la Vivienda <span className="text-red-600">*</span></span>
            <input
              type="number" min="0" step="0.01" placeholder="€"
              value={state.capital_propio ?? ""}
              onChange={(e) => setState((s) => ({ ...s, capital_propio: e.target.value }))}
              onBlur={() => persist()}
              data-testid="concurrencia-capital-propio"
              className="border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-[11px]">Total Capital asegurado de la Comunidad <span className="text-red-600">*</span></span>
            <input
              type="number" min="0" step="0.01" placeholder="€"
              value={p0.capital ?? ""}
              onChange={(e) => ensureFirst({ capital: e.target.value })}
              onBlur={() => persist()}
              data-testid="concurrencia-capital-contraria"
              className="border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
            />
          </label>
        </div>
      </div>

      {/* — SECCIÓN 4: TOTAL Daños — */}
      <div>
        <div className="font-display font-bold text-sm mb-2">TOTAL Daños</div>
        <label className="flex flex-col gap-1 text-[12px] max-w-sm">
          <span className="text-[11px]">TOTAL Daños <span className="text-red-600">*</span></span>
          <input
            type="number" min="0" step="0.01" placeholder="€"
            value={state.base_importe ?? ""}
            onChange={(e) => setState((s) => ({ ...s, base_importe: e.target.value }))}
            onBlur={() => persist()}
            data-testid="concurrencia-base-importe"
            className="border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
          />
        </label>
      </div>

      {/* — SECCIÓN 5: OUTPUTS (Capital asegurado / % Asumido / Importes) — */}
      {capTotal > 0 && (
        <div className="space-y-5 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="concurrencia-preview">
          <div>
            <div className="font-display font-bold text-sm mb-2">Capital asegurado de la Vivienda</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="text-[11px]">Capital asegurado de la Vivienda</span>
                <input readOnly type="text" value={fmtEUR(capViv)}
                  className="border border-border rounded-md px-3 py-2 text-sm bg-white text-navy font-semibold" />
              </label>
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="text-[11px]">Capital asegurado Total</span>
                <input readOnly type="text" value={fmtEUR(capTotal)}
                  data-testid="concurrencia-out-capital-total"
                  className="border border-border rounded-md px-3 py-2 text-sm bg-white text-navy font-semibold" />
              </label>
            </div>
          </div>

          <div>
            <div className="font-display font-bold text-sm mb-2">% Asumido por cada Cía</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="text-[11px]">% Asumido por la Vivienda</span>
                <input readOnly type="text" value={fmtPct(pctAsumidoViv)}
                  data-testid="concurrencia-out-pct-vivienda"
                  className="border border-border rounded-md px-3 py-2 text-sm bg-white text-emerald-700 font-bold" />
              </label>
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="text-[11px]">% Asumido por la Comunidad</span>
                <input readOnly type="text" value={fmtPct(pctAsumidoCom)}
                  data-testid="concurrencia-out-pct-comunidad"
                  className="border border-border rounded-md px-3 py-2 text-sm bg-white text-sky-700 font-bold" />
              </label>
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="text-[11px]">% Asumido Total</span>
                <input readOnly type="text" value="100,00 %"
                  className="border border-border rounded-md px-3 py-2 text-sm bg-white text-navy font-semibold" />
              </label>
            </div>
          </div>

          <div>
            <div className="font-display font-bold text-sm mb-2">Importe a pagar por cada Cía</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="text-[11px]">Importe a pagar Vivienda <span className="text-muted">({aseguradoraPropia})</span></span>
                <input readOnly type="text" value={fmtEUR(importeViv)}
                  data-testid="concurrencia-out-importe-vivienda"
                  className="border border-border rounded-md px-3 py-2 text-base bg-white text-emerald-700 font-bold" />
              </label>
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="text-[11px]">Importe a pagar Comunidad <span className="text-muted">({p0.aseguradora_nombre || "—"})</span></span>
                <input readOnly type="text" value={fmtEUR(importeCom)}
                  data-testid="concurrencia-out-importe-comunidad"
                  className="border border-border rounded-md px-3 py-2 text-base bg-white text-sky-700 font-bold" />
              </label>
              <label className="flex flex-col gap-1 text-[12px]">
                <span className="text-[11px]">Total</span>
                <input readOnly type="text" value={fmtEUR(danos)}
                  className="border border-border rounded-md px-3 py-2 text-base bg-white text-navy font-bold" />
              </label>
            </div>
          </div>
        </div>
      )}

      <label className="flex flex-col gap-1 text-[12px]">
        <span className="font-semibold text-muted uppercase tracking-widest text-[10px]">Notas para el informe (opcional)</span>
        <textarea
          rows={2}
          value={state.notas || ""}
          onChange={(e) => setState((s) => ({ ...s, notas: e.target.value }))}
          onBlur={() => persist()}
          data-testid="concurrencia-notas"
          placeholder="Ej.: La comunidad asegura únicamente los elementos comunes; la vivienda tiene póliza propia."
          className="border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-foreground/20 focus:border-foreground"
        />
      </label>
    </div>
  );
}

/**
 * Sección «Concurrencia con otras aseguradoras» — bloque plegable.
 * Cálculo (art. 32 LCS): aportación = base × (capital / Σ capitales).
 * La aseguradora del expediente se toma de `extracted_data`; el perito
 * edita SU capital y añade las otras aseguradoras concurrentes.
 */
function ConcurrenciaSection({ exp, onReload }) {
  const initial = exp.concurrencia || {
    activa: false, base_importe: 0, capital_propio: 0,
    participantes: [], notas: "",
  };
  const [state, setState] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(!!initial.activa);
  const ed = exp.extracted_data || {};
  const aseguradoraPropia =
    ed.aseguradora_nombre || ed.aseguradora || ed.compania || "Aseguradora del expediente";

  useEffect(() => {
    setState(exp.concurrencia || {
      activa: false, base_importe: 0, capital_propio: 0, participantes: [], notas: "",
    });
    setOpen(!!(exp.concurrencia && exp.concurrencia.activa));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exp.id]);

  const persist = async (override) => {
    setSaving(true);
    try {
      const next = { ...state, ...(override || {}) };
      const payload = {
        concurrencia: {
          activa: !!next.activa,
          base_importe: Number(next.base_importe) || 0,
          capital_propio: Number(next.capital_propio) || 0,
          // La calculadora Innoku no aplica coeficiente al capital de la
          // vivienda propia (siempre 100 %). Forzamos null para evitar
          // que quede un valor legacy en Mongo que descuadraría el PDF.
          coeficiente_propio: null,
          participantes: (next.participantes || []).map((p) => ({
            aseguradora_nombre: (p.aseguradora_nombre || "").trim(),
            poliza: (p.poliza || "").trim() || null,
            capital: Number(p.capital) || 0,
            coeficiente_participacion:
              p.coeficiente_participacion == null || p.coeficiente_participacion === ""
                ? null
                : Number(p.coeficiente_participacion),
          })),
          notas: next.notas || null,
        },
      };
      await api.put(`/expedientes/${exp.id}`, payload);
      setState(next);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la concurrencia");
    } finally {
      setSaving(false);
    }
  };

  const updateParticipante = (idx, patch) => {
    setState((s) => {
      const arr = [...(s.participantes || [])];
      arr[idx] = { ...arr[idx], ...patch };
      return { ...s, participantes: arr };
    });
  };

  return (
    <section className="border border-slate-200 rounded-xl bg-white overflow-hidden mt-4" data-testid="concurrencia-section">
      <header
        className="px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 border-b border-slate-200"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2">
          {open ? <CaretDown className="w-4 h-4" weight="bold" /> : <CaretRight className="w-4 h-4" weight="bold" />}
          <Buildings className="w-4 h-4 text-sky-600" weight="duotone" />
          <div>
            <div className="font-display font-bold text-sm tracking-tight">Concurrencia con otras aseguradoras</div>
            <div className="text-[10.5px] text-muted">art. 32 LCS — prorrateo del importe según el capital asegurado</div>
          </div>
        </div>
        {state.activa && (
          <span className="text-[10px] uppercase tracking-widest font-bold text-white bg-sky-600 rounded px-2 py-0.5">Activa</span>
        )}
      </header>

      {open && (
        <div className="p-5 space-y-4">
          <label className="flex items-center gap-2 text-[13px] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!state.activa}
              onChange={(e) => persist({ activa: e.target.checked })}
              data-testid="concurrencia-activa"
              className="w-4 h-4"
            />
            <span className="font-semibold">Aplicar concurrencia en este expediente</span>
          </label>

          {state.activa && (
            <ConcurrenciaInnokuForm
              state={state}
              setState={setState}
              persist={persist}
              updateParticipante={updateParticipante}
              aseguradoraPropia={aseguradoraPropia}
            />
          )}
        </div>
      )}
    </section>
  );
}
